/* Loupe – announcement bar: a fading one-at-a-time slider or a seamless marquee ticker.
   Pauses on hover and keyboard focus; no automatic motion with prefers-reduced-motion. */
(() => {
  // the first scroll, tap, key or mouse move – rotation / pop-ups wait for it (idle tabs and page-speed tests see a calm page)
  const firstInput = (fn) => {
    if (window.Shopify && Shopify.designMode) return fn();
    const ev = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'wheel'];
    const go = () => { ev.forEach((e) => removeEventListener(e, go, true)); fn(); };
    ev.forEach((e) => addEventListener(e, go, { passive: true, capture: true }));
  };
  if (customElements.get('loupe-announcement-bar')) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const PX_PER_S = { slow: 28, normal: 45, fast: 72 };

  class LoupeAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.holds = new Set();
      if (this.dataset.layout === 'marquee') this.marquee();
      else this.slider();
    }

    disconnectedCallback() {
      this.ac.abort();
      clearTimeout(this.t);
      if (this.ro) this.ro.disconnect();
    }

    /* ---------- slider ---------- */
    slider() {
      const o = { signal: this.ac.signal };
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.live = this.querySelector('[data-slides]');
      this.i = 0;
      if (this.slides.length < 2) return;
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-dir]');
        if (!b) return;
        this.live.setAttribute('aria-live', 'polite');
        this.go(this.i + +b.dataset.dir);
      }, o);
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hold('hover', true); }, o);
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.hold('hover', false); }, o);
      this.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) this.hold('focus', true); }, o);
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold('focus', false); }, o);
      document.addEventListener('visibilitychange', () => this.hold('hidden', document.hidden), o);
      this.addEventListener('shopify:block:select', (e) => {
        const n = this.slides.indexOf(e.target.closest('[data-slide]'));
        if (n > -1) this.go(n);
        this.hold('editor', true);
      }, o);
      this.addEventListener('shopify:block:deselect', () => this.hold('editor', false), o);
      if (reduce.matches) this.holds.add('reduce');
      this.holds.add('idle'); if (this.dataset.layout === 'marquee') this.classList.add('is-held');
      firstInput(() => this.hold('idle', false));
      this.restart();
    }

    hold(reason, on) {
      if (on === this.holds.has(reason)) return;
      on ? this.holds.add(reason) : this.holds.delete(reason);
      if (this.dataset.layout === 'marquee') this.classList.toggle('is-held', this.holds.size > 0);
      else this.restart();
    }

    restart() {
      clearTimeout(this.t);
      if (this.holds.size) return;
      this.t = setTimeout(() => this.go(this.i + 1), (parseFloat(this.dataset.speed) || 5) * 1000);
    }

    go(n) {
      const len = this.slides.length;
      n = ((n % len) + len) % len;
      if (n !== this.i) {
        const a = this.slides[this.i], b = this.slides[n];
        a.classList.remove('is-active'); a.setAttribute('aria-hidden', 'true'); a.inert = true;
        b.classList.add('is-active'); b.removeAttribute('aria-hidden'); b.inert = false;
        this.i = n;
      }
      this.restart();
    }

    /* ---------- marquee ---------- */
    marquee() {
      const o = { signal: this.ac.signal };
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      const px = PX_PER_S[this.dataset.marqueeSpeed] || PX_PER_S.normal;
      const fit = () => {
        const groups = this.track.querySelectorAll('[data-group]');
        const w = groups[0].getBoundingClientRect().width;
        if (!w) return;
        /* enough copies to cover the bar plus one group, so the loop never shows a gap */
        const need = Math.ceil(this.clientWidth / w) + 1;
        for (let k = groups.length; k < need; k++) this.track.append(groups[groups.length - 1].cloneNode(true));
        this.track.style.setProperty('--shift', `${w}px`);
        this.track.style.setProperty('--dur', `${(w / px).toFixed(2)}s`);
      };
      this.ro = new ResizeObserver(fit);
      this.ro.observe(this);
      this.ro.observe(this.track.querySelector('[data-group]'));
      this.addEventListener('shopify:block:select', () => this.hold('editor', true), o);
      this.addEventListener('shopify:block:deselect', () => this.hold('editor', false), o);
    }
  }

  customElements.define('loupe-announcement-bar', LoupeAnnouncementBar);
})();
